import { apiDjango } from './ApiDjango'
import { ListResponse } from '../interfaces/Shipment/ListResponseDto'
import { InventoryItemDto } from '../interfaces/Inventory/InventoryItemDto'
import { CreateOrderRequest } from '../interfaces/Inventory/CreateOrderRequest'
import { CreateOrderResponse } from '../interfaces/Inventory/CreateOrderResponse'
import { InventoryItemMapperDto } from '../utils/mappers'
import { InventoryApiProduct } from '../interfaces/Inventory/InventoryQuery'

const { REACT_APP_URL_PATH_API_DJANGO } = process.env
// DTOs devueltos por el API Django
interface InventoryDataDtoRaw {
  owner_id: number
  owner_name: string
  products: InventoryApiProduct[]
}

type InventoryApiResponse =
  | {
      code: string
      error: false
      message: string
      data: InventoryDataDtoRaw
      error_data: Record<string, unknown>
    }
  | {
      code: string
      error: true
      message: string
      data: null
      error_data: { error?: string } | Record<string, unknown>
    }

/**
 * Busca inventario en el API Django.
 * @param identificationNumber Cadena en formato `${abbr}-${id}` por ejemplo `J-404044041`.
 * @param listProducts Lista de IDs de productos o null para traer todos.
 */
export async function SearchInventory(
  identificationNumber: string,
  listProducts?: Array<number | string> | null
): Promise<ListResponse<InventoryItemDto[]>> {
  try {
    const body = {
      owner_identification: identificationNumber,
      product_id: listProducts ?? null,
    }
    const url = `${REACT_APP_URL_PATH_API_DJANGO}/odoo/inventory`
    const resp = await apiDjango.get<InventoryApiResponse>(url, {
      params: body,
    })

    if (
      resp.status === 200 &&
      resp.data &&
      (resp.data as any).error === false
    ) {
      const ok = resp.data as any
      const raw = ok.data as InventoryDataDtoRaw
      return {
        message: ok.message ?? '',
        didError: false,
        errorMessage: '',
        model: Array.isArray(raw.products)
          ? raw.products.map((p: InventoryApiProduct) =>
              InventoryItemMapperDto(p)
            )
          : [],
      }
    }

    const errMessage =
      (resp.data as any)?.message ||
      (resp.data as any)?.error_data?.error ||
      'No se pudo obtener el inventario'

    return {
      message: errMessage,
      didError: true,
      errorMessage: errMessage,
      model: null,
    }
  } catch (error: any) {
    const msg = error?.message || 'Error llamando al API de inventario'
    return {
      message: msg,
      didError: true,
      errorMessage: msg,
      model: null,
    }
  }
}

// --- Crear orden(es) en API de órdenes ---
type CreateOrderApiResponse =
  | CreateOrderResponse
  | {
      code: string
      error: true
      message: string
      data: null
      error_data: { error?: string } | Record<string, unknown>
    }

/**
 * Crea una o varias órdenes en el API de órdenes local.
 * POST http://localhost:8000/api/orders
 */
export async function CreateOrdern(
  payload: CreateOrderRequest
): Promise<ListResponse<CreateOrderResponse['data']>> {
  try {
    const url = `${REACT_APP_URL_PATH_API_DJANGO}/odoo/delivery-order`
    const resp = await apiDjango.post<CreateOrderApiResponse>(url, payload)
    if (
      resp.status === 200 &&
      resp.data &&
      (resp.data as any).error === false
    ) {
      const ok = resp.data as CreateOrderResponse
      return {
        message: ok.message ?? 'OK',
        didError: false,
        errorMessage: '',
        model: ok.data,
      }
    }
    const errMessage =
      (resp.data as any)?.message ||
      (resp.data as any)?.error_data?.error ||
      'No se pudo crear la orden'
    return {
      message: errMessage,
      didError: true,
      errorMessage: errMessage,
      model: null,
    }
  } catch (error: any) {
    const msg = error?.message || 'Error llamando al API de órdenes'
    return {
      message: msg,
      didError: true,
      errorMessage: msg,
      model: null,
    }
  }
}
